import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "./AuthContext";

const PlatformContext = createContext(null);
const STORAGE_KEY = "myv_platform_context";
const TOKEN_KEY = "myv_token";
const TOKEN_EXPIRES_KEY = "myv_token_expires_at";

const readStorageValue = (storage, key) => {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const writeStorageValue = (storage, key, value) => {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // no-op
  }
};

const clearStorageValue = (storage, key) => {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch (error) {
    // no-op
  }
};

const getLocalStorage = () =>
  typeof window !== "undefined" ? window.localStorage : null;

const getSessionStorage = () =>
  typeof window !== "undefined" ? window.sessionStorage : null;

const readStoredContext = () => {
  clearStorageValue(getLocalStorage(), STORAGE_KEY);
  return readStorageValue(getSessionStorage(), STORAGE_KEY);
};

const writeStoredContext = (value) => {
  writeStorageValue(getSessionStorage(), STORAGE_KEY, value);
  clearStorageValue(getLocalStorage(), STORAGE_KEY);
};

const clearStoredContext = () => {
  clearStorageValue(getSessionStorage(), STORAGE_KEY);
  clearStorageValue(getLocalStorage(), STORAGE_KEY);
};

const readToken = () => readStorageValue(getLocalStorage(), TOKEN_KEY);

const readExpiresAt = () => readStorageValue(getLocalStorage(), TOKEN_EXPIRES_KEY);

const applyPreferences = (preferences) => {
  if (typeof document === "undefined" || !preferences) return;
  const root = document.documentElement;
  if (preferences.theme) {
    root.dataset.theme = preferences.theme;
  }
  if (preferences.accentColor) {
    root.style.setProperty("--myv-accent", preferences.accentColor);
  }
  if (preferences.language) {
    root.lang = preferences.language;
  }
};

export const ENTRY_POINT_ROUTES = {
  PAYMENT: "/billing",
  PAYMENT_REQUIRED: "/billing",
  PROFILE_SETUP: "/profile/setup",
  DASHBOARD: "/dashboard",
};

export const resolveEntryPointRoute = (entryPoint) =>
  ENTRY_POINT_ROUTES[entryPoint] || null;

export const resolvePostLoginRoute = (context) => {
  const session = context?.session || {};
  const requiresPayment = session?.requiresPayment === true;
  const forcedRoute = resolveEntryPointRoute(session?.forceAction);
  const entryRoute = resolveEntryPointRoute(session?.entryPoint);
  if (requiresPayment) return "/billing";
  if (forcedRoute) return forcedRoute;
  return entryRoute || "/dashboard";
};

export const isFeatureEnabled = (features, key) => {
  if (!features || !key) return false;
  return key.split(".").reduce((acc, part) => acc?.[part], features) === true;
};

export function PlatformContextProvider({ children }) {
  const { user: authUser } = useAuth();
  const [contextState, setContextState] = useState(() => readStoredContext());
  const [status, setStatus] = useState(contextState ? "ready" : "idle");
  const [isLoading, setIsLoading] = useState(!contextState);
  const [error, setError] = useState(null);

  const setPlatformContext = useCallback((value) => {
    if (!value) {
      clearStoredContext();
      setContextState(null);
      setStatus("missing");
      return;
    }
    setContextState(value);
    writeStoredContext(value);
    setStatus("ready");
    applyPreferences(value?.preferences);
  }, []);

  useEffect(() => {
    const token = readToken();
    const expiresAt = readExpiresAt();
    const isExpired = typeof expiresAt === "number" && Date.now() > expiresAt;
    if (!token || isExpired) {
      clearStoredContext();
      setContextState(null);
      setStatus("idle");
      setIsLoading(false);
      return;
    }
    if (!contextState) {
      setStatus("missing");
      setIsLoading(false);
      return;
    }
    setStatus("ready");
    setIsLoading(false);
  }, [contextState]);

  useEffect(() => {
    if (!authUser) {
      clearStoredContext();
      setContextState(null);
        setStatus("idle");
    }
  }, [authUser]);

  useEffect(() => {
    applyPreferences(contextState?.preferences);
  }, [contextState?.preferences]);

  const value = useMemo(
    () => ({
      user: contextState?.user || null,
      userEmail: contextState?.user?.email ?? null,
      userPhone: contextState?.user?.phone ?? null,
      userStatus: contextState?.user?.status ?? null,
      userCreatedAt: contextState?.user?.createdAt ?? null,
      usage: contextState?.usage || null,
      locale: contextState?.locale || null,
      localeRegion: contextState?.locale?.region ?? null,
      localeCountry: contextState?.locale?.country ?? null,
      localeCurrency: contextState?.locale?.currency ?? null,
      session: contextState?.session || null,
      uiHints: contextState?.uiHints || null,
      features: contextState?.features || null,
      security: contextState?.security || null,
      securityMfaEnabled: contextState?.security?.mfaEnabled ?? null,
      securitySessionTimeout: contextState?.security?.sessionTimeout ?? null,
      securityPasswordExpiryDays: contextState?.security?.passwordExpiryDays ?? null,
      preferences: contextState?.preferences || null,
      entryPoint: contextState?.session?.entryPoint || null,
      status,
      isLoading,
      error,
      setPlatformContext,
    }),
    [contextState, status, isLoading, error, setPlatformContext]
  );

  return (
    <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>
  );
}

export const usePlatformContext = () => useContext(PlatformContext);

export const clearPlatformContextStorage = clearStoredContext;
